"use client";

import React, { useState } from "react";
import Image from "next/image";
import BrochurePopupForm from "@/components/newPageComponent/CommonSection/BrochurePopupForm";

/* ---------------- TYPES ---------------- */
type CardId = "far" | "aud" | "reg" | "bar";

interface ExamCard {
  id: CardId;
  badge: string;
  image: string;
  title: string;
  points: string[];
}

/* ---------------- ICON ---------------- */
const CheckIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{ flexShrink: 0, marginTop: "0.5px" }}
  >
    <circle cx="10" cy="10" r="9" stroke="#3399ff" strokeWidth="1.5" />
    <path
      d="M6.5 10L9 12.5L13.5 7.5"
      stroke="#3399ff"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/* ---------------- DATA ---------------- */
const examCards: ExamCard[] = [
  {
    id: "far",
    badge: "EA PART 1",
    image:
      "https://ik.imagekit.io/mileseducation/miles_website/ea/new/individual.webp",
    title: "Individual",
    points: [
      "Preliminary Work with Taxpayer Data",
      "Taxation",
      "Income and Assets",
      "Advising the Individual Taxpayer",
      "Deductions & Credits",
      "Specialized Returns for Individuals",
    ],
  },
  {
    id: "bar",
    badge: "EA PART 2",
    image:
      "https://ik.imagekit.io/mileseducation/miles_website/ea/new/businesses.webp",
    title: "Businesses",
    points: [
      "Business Entities and Considerations",
      "Specialized Returns and Taxpayers",
      "Business Tax Preparation",
    ],
  },
  {
    id: "aud",
    badge: "EA PART 3",
    image:
      "https://ik.imagekit.io/mileseducation/miles_website/ea/new/procedures.webp",
    title: "Representation, Practice & Procedures",
    points: [
      "Practices and Procedures",
      "Specific Areas of Representation",
      "Representation before the IRS",
      "Filing Process",
    ],
  },
];

/* ---------------- STYLES ---------------- */
const badgeStyles: Record<CardId, string> = {
  far: "text-[#084b5d] bg-[#69c5de33]",
  aud: "text-[#355209] bg-[#8dc53933]",
  reg: "text-[#4c110c] bg-[#ef5c5033]",
  bar: "text-[#3f2b07] bg-[#fbb73b33]",
};

/* ---------------- COMPONENT ---------------- */
const CpaEquivalentNew = () => {
  const [isFormPopupOpen, setFormPopupOpen] = useState(false);

  const openFormPopup = () => {
    if (typeof window !== "undefined") {
      sessionStorage.setItem("modal-open", "true");
    }
    setFormPopupOpen(true);
  };

  const closeFormPopup = () => {
    if (typeof window !== "undefined") {
      sessionStorage.removeItem("modal-open");
    }
    setFormPopupOpen(false);
  };

  return (
    <>
      <section className="bg-white py-8 px-4 max-md:bg-[#f3f3f3] max-md:p-4 max-md:pt-0 max-md:pb-7">
        <div className="main_container py-5 bg-white rounded-2xl">
          <div className="flex flex-col items-center text-center">
            <h2 className="m-0 text-[#333] text-[32px] font-bold leading-[1.2] tracking-[-0.02em] max-md:text-[24px]">
              <span className="text-[#3399ff]">U.S. EA</span> = U.S. Tax
              Practitioner
            </h2>

            <p className="mt-8 text-[#999] text-sm font-semibold max-md:text-xs max-md:mt-2.5">
              3 Parts | 15 Months | Integrated with
              <br className="md:hidden block" /> CAIRA - AI workflows & agents
            </p>

            {/* CARDS */}
            <div className="grid grid-cols-[repeat(auto-fit,minmax(325px,280px))] justify-center gap-4 w-full mt-8 max-md:flex max-md:overflow-x-auto max-md:gap-3 md:px-4 px-0 max-md:[scrollbar-width:none] max-md:justify-start">
              {examCards.map((card) => (
                <div
                  key={card.id}
                  className="flex flex-col border border-[#dee7f5] rounded-xl bg-white shadow-sm p-2.5 max-md:w-[270px] max-md:flex-shrink-0"
                >
                  <Image
                    src={card.image}
                    alt={card.title}
                    width={320}
                    height={180}
                    sizes="(max-width: 768px) 270px, 320px"
                    className="w-full h-auto"
                  />

                  <div className="pt-3 flex flex-col gap-2">
                    <div className="flex items-center justify-center gap-2 flex-wrap">
                      <span
                        className={`rounded-full px-2.5 py-[5px] text-xs font-bold ${badgeStyles[card.id]}`}
                      >
                        {card.badge}
                      </span>

                      <span className="text-[11px] font-semibold text-[#39f] flex items-center">
                        Integrated with
                        <Image
                          src="https://ik.imagekit.io/mileseducation/miles_website/accounting/cpa-visit-new/starIcon.webp"
                          alt="star"
                          width={12}
                          height={12}
                          className="mx-1"
                        />
                        CAIRA
                      </span>
                    </div>

                    <h3 className="text-[#0B2A4D] text-base font-bold text-center">
                      {card.title}
                    </h3>

                    <ul className="flex flex-col gap-1.5">
                      {card.points.map((point) => (
                        <li
                          key={point}
                          className="flex items-start gap-1.5 md:text-sm text-xs text-[#333333]"
                        >
                          <CheckIcon />
                          {point}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>

            {/* BUTTON */}
            <button
              className="mt-8 !min-w-[306px] px-[28px] py-[12px] border-0 rounded-tl-[10.85px] rounded-tr-[10.85px] rounded-br-[10.85px] rounded-bl-none bg-[#2a85ff] text-white text-[16px] font-bold leading-none cursor-pointer max-md:w-[calc(100%-32px)] max-md:min-w-0 max-md:px-4 max-md:text-[15px]"
              onClick={openFormPopup}
            >
              Download Brochure
            </button>
          </div>
        </div>
      </section>

      {/* POPUP */}
      {isFormPopupOpen && (
        <BrochurePopupForm handleClose={closeFormPopup} pageName="EA" />
      )}
    </>
  );
};

export default CpaEquivalentNew;
